/**
 * Base interfaces for dashboard data structure
 * Separates time-range-based data from non-time-range data
 */

// ==================== Common Types ====================

export interface ITimeRange {
  startDate: string;
  endDate: string;
}

export interface ITimeRangeParams {
  startDate?: string;
  endDate?: string;
  period?: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly' | 'custom';
}

// ==================== Finance Dashboard Types ====================

/**
 * Finance Action Items (Present/TODOs) - No time range needed
 */
export interface IFinanceActionItem {
  id: string;
  type: 'overdue' | 'partial' | 'failed_payment' | 'refund_request';
  title: string;
  description?: string;
  amount: number;
  count: number;
  priority: 'high' | 'medium' | 'low';
  dueDate?: string;
  relatedEntityId?: string; // student/staff ID
  relatedEntityName?: string;
  navigationPath?: string; // Path to navigate when clicked
}

export interface IFinanceActionItems {
  overdue: {
    totalAmount: number;
    totalCount: number;
    items: IFinanceActionItem[];
  };
  partialPayments: {
    totalAmount: number;
    totalCount: number;
    items: IFinanceActionItem[];
  };
  paymentIssues: {
    failedPayments: number;
    refundRequests: number;
    items: IFinanceActionItem[];
  };
}

/**
 * Finance Upcoming Items (Future) - No time range needed (calculated from due dates)
 */
export interface IFinanceUpcomingItem {
  id: string;
  type: 'fee_due' | 'scholarship_disbursement' | 'grant_deadline' | 'report_due';
  title: string;
  description?: string;
  dueDate: string;
  amount?: number;
  count?: number;
  navigationPath?: string;
}

export interface IFinanceUpcomingItems {
  feeDeadlines: IFinanceUpcomingItem[];
  scholarshipDisbursements: IFinanceUpcomingItem[];
  grantDeadlines: IFinanceUpcomingItem[];
  reportDeadlines: IFinanceUpcomingItem[];
}

/**
 * Finance Summary (Past) - Time range based
 */
export interface IFinanceSummary {
  totalCollected: number;
  totalDue: number;
  collectionRate: number; // percentage
  targetAmount?: number;
  targetAchievement?: number; // percentage
  transactions: {
    total: number;
    count: number;
    byType: {
      fees: number;
      income: number;
      expense: number;
    };
  };
  payments: {
    successful: number;
    failed: number;
    refunded: number;
    cancelled: number;
  };
  status: {
    paid: number;
    partial: number;
    unpaid: number;
    overdue: number;
  };
}

export interface IFinanceRecentActivity {
  id: string;
  type: 'collection' | 'refund' | 'expense' | 'payment';
  description: string;
  amount: number;
  timestamp: string;
  relatedEntityName?: string;
  navigationPath?: string;
}

/**
 * Complete Finance Dashboard Data
 */
export interface IFinanceDashboardData {
  // Present - Action Items (no time range)
  actionItems: IFinanceActionItems;
  
  // Future - Upcoming Items (no time range)
  upcomingItems: IFinanceUpcomingItems;
  
  // Past - Summary (time range based)
  summary: IFinanceSummary;
  recentActivity: IFinanceRecentActivity[];
  
  // Time range for summary data
  timeRange?: ITimeRange;
}

// ==================== People Dashboard Types ====================

/**
 * People Action Items (Present/TODOs) - No time range needed
 */
export interface IPeopleActionItem {
  id: string;
  type: 'attendance_missing' | 'unexcused_absence' | 'leave_request' | 'document_expired' | 'health_record_missing';
  title: string;
  description?: string;
  count: number;
  priority: 'high' | 'medium' | 'low';
  relatedEntityId?: string; // student/staff ID
  relatedEntityName?: string;
  navigationPath?: string;
}

export interface IPeopleActionItems {
  attendanceIssues: {
    todayAbsentees: number;
    unexcusedAbsences: number;
    items: IPeopleActionItem[];
  };
  leaveRequests: {
    pendingCount: number;
    items: IPeopleActionItem[];
  };
  documentationGaps: {
    expiredDocuments: number;
    missingHealthRecords: number;
    incompleteProfiles: number;
    items: IPeopleActionItem[];
  };
}

/**
 * People Upcoming Items (Future) - No time range needed (calculated from dates)
 */
export interface IPeopleUpcomingItem {
  id: string;
  type: 'work_anniversary' | 'birthday' | 'contract_renewal' | 'performance_review' | 'parent_teacher_meeting';
  title: string;
  description?: string;
  date: string;
  relatedEntityId?: string;
  relatedEntityName?: string;
  years?: number; // for anniversaries
  navigationPath?: string;
}

export interface IPeopleUpcomingItems {
  milestones: IPeopleUpcomingItem[]; // anniversaries, birthdays
  meetings: IPeopleUpcomingItem[]; // parent-teacher meetings
  reviews: IPeopleUpcomingItem[]; // performance reviews, contract renewals
}

/**
 * People Summary (Past) - Time range based
 */
export interface IPeopleSummary {
  students: {
    total: number;
    active: number;
    inactive: number;
    newEnrollments: number;
    attendanceRate: number; // percentage
  };
  staff: {
    total: number;
    active: number;
    onLeave: number;
    attendanceRate: number; // percentage
  };
  attendance: {
    totalPresent: number;
    totalAbsent: number;
    totalLeave: number;
    overallRate: number; // percentage
  };
}

export interface IPeopleRecentActivity {
  id: string;
  type: 'enrollment' | 'staff_joining' | 'attendance_marked' | 'leave_approved';
  description: string;
  timestamp: string;
  relatedEntityName?: string;
  navigationPath?: string;
}

/**
 * Complete People Dashboard Data
 */
export interface IPeopleDashboardData {
  // Present - Action Items (no time range)
  actionItems: IPeopleActionItems;
  
  // Future - Upcoming Items (no time range)
  upcomingItems: IPeopleUpcomingItems;
  
  // Past - Summary (time range based)
  summary: IPeopleSummary;
  recentActivity: IPeopleRecentActivity[];
  
  // Time range for summary data
  timeRange?: ITimeRange;
}

// ==================== Events Dashboard Types ====================

export enum EventType {
  HOLIDAY = 'holiday',
  MEETING = 'meeting',
  EXAM = 'exam',
  SPORT = 'sport',
  CULTURAL = 'cultural',
  WORKSHOP = 'workshop',
  SEMINAR = 'seminar',
  OTHER = 'other',
}

/**
 * Events Action Items (Present/TODOs) - No time range needed
 */
export interface IEventsActionItem {
  id: string;
  type: 'event_today' | 'setup_pending' | 'approval_request' | 'followup_pending';
  title: string;
  description?: string;
  eventId: string;
  eventName: string;
  priority: 'high' | 'medium' | 'low';
  dueTime?: string;
  checklistItems?: string[];
  navigationPath?: string;
}

export interface IEventsActionItems {
  todayEvents: {
    count: number;
    ongoing: number;
    upcoming: number;
    items: IEventsActionItem[];
  };
  todos: {
    pending: number;
    inProgress: number;
    items: IEventsActionItem[];
  };
}

/**
 * Events Upcoming Items (Future) - No time range needed (calculated from event dates)
 */
export interface IEventsUpcomingItem {
  id: string;
  type: EventType;
  title: string;
  description?: string;
  startDate: string;
  endDate?: string;
  startTime?: string;
  endTime?: string;
  location?: string;
  registrationDeadline?: string;
  submissionDeadline?: string;
  rsvpDeadline?: string;
  navigationPath?: string;
}

export interface IEventsUpcomingItems {
  events: IEventsUpcomingItem[];
  deadlines: IEventsUpcomingItem[]; // registration, submission, RSVP deadlines
}

/**
 * Calendar Event for Calendar Widget
 */
export interface ICalendarEvent {
  id: string;
  type: EventType;
  title: string;
  date: string;
  startTime?: string;
  endTime?: string;
  color?: string; // for calendar display
  navigationPath?: string;
}

/**
 * Events Summary (Past) - Time range based
 */
export interface IEventsSummary {
  totalEvents: number;
  completed: number;
  cancelled: number;
  completionRate: number; // percentage
  averageAttendance?: number;
  byType: {
    [key in EventType]?: number;
  };
}

export interface IEventsRecentActivity {
  id: string;
  type: 'event_completed' | 'event_cancelled' | 'event_created';
  description: string;
  eventName: string;
  timestamp: string;
  navigationPath?: string;
}

/**
 * Complete Events Dashboard Data
 */
export interface IEventsDashboardData {
  // Present - Action Items (no time range)
  actionItems: IEventsActionItems;
  
  // Future - Upcoming Items (no time range)
  upcomingItems: IEventsUpcomingItems;
  
  // Calendar data for widget
  calendarEvents: ICalendarEvent[];
  
  // Past - Summary (time range based)
  summary: IEventsSummary;
  recentActivity: IEventsRecentActivity[];
  
  // Time range for summary data
  timeRange?: ITimeRange;
}
